import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, Modal, Form, Input, Select, message, Descriptions, Timeline, Spin, Divider, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { getOrganizationList, createOrganization, updateOrganization, deleteOrganization, getOrganizationDetail } from '../../api/organization';
import type { Organization, CreateOrganizationRequest, UpdateOrganizationRequest, GetOrganizationListRequest } from '../../DTO/organization';

const { Title } = Typography;
const { TextArea } = Input;

const OrganizationManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);
  const [detailOrganization, setDetailOrganization] = useState<Organization | null>(null);
  const [operationLogs, setOperationLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 获取组织列表
  const fetchOrganizationList = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const params: GetOrganizationListRequest = {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        order: 'desc',
      };

      const response = await getOrganizationList(params);
      if (response.code === 0) {
        setOrganizations(response.data.data);
        setPagination((prev) => ({
          ...prev,
          pageSize: pageSize,
          current: page,
          total: response.data.total,
        }));
      } else {
        message.error(response.message || '获取组织列表失败');
      }
    } catch (error) {
      console.error('获取组织列表失败:', error);
      message.error('获取组织列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchOrganizationList();
  }, []);

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    fetchOrganizationList(pagination.current, pagination.pageSize);
  };

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '名称',
      dataIndex: 'organization_name',
      key: 'organization_name',
      sorter: (a: Organization, b: Organization) => a.organization_name.localeCompare(b.organization_name),
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: '创建人',
      dataIndex: 'created_by',
      key: 'created_by',
      render: (created_by: string) => created_by || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: string) => {
        if (created_at) {
          return new Date(created_at).toLocaleString();
        }
        return '-';
      },
      sorter: (a: Organization, b: Organization) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 200,
      render: (_: any, record: Organization) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除这个组织吗？" description="删除后该组织下的用户将失去关联" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingOrganization(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleViewDetail = async (organization: Organization) => {
    try {
      setDetailOrganization(organization);
      setIsDetailModalOpen(true);

      // 获取组织详情
      const response = await getOrganizationDetail({ id: organization.id });
      if (response.code === 0) {
        setDetailOrganization(response.data);
      } else {
        message.error(response.message || '获取组织详情失败');
      }

      // 模拟加载操作记录
      fetchOperationLogs(organization.id);
    } catch (error) {
      console.error('获取组织详情失败:', error);
      message.error('获取组织详情失败');
    }
  };

  const handleEdit = (organization: Organization) => {
    setEditingOrganization(organization);
    form.setFieldsValue({
      organization_name: organization.organization_name,
      phone: organization.phone,
      email: organization.email,
      status: organization.status,
    });
    setIsModalOpen(true);
  };

  const handleEditFromDetail = (organization: Organization) => {
    setIsDetailModalOpen(false);
    handleEdit(organization);
  };

  const fetchOperationLogs = async (organizationId: number) => {
    setLogsLoading(true);
    setTimeout(() => {
      const mockLogs = [
        {
          id: 1,
          organizationId,
          operator: '超级管理员张一一',
          action: '编辑了组织/机构名称',
          time: '17/08/2025 09:10:38',
          type: 'edit',
        },
        {
          id: 2,
          organizationId,
          operator: '超级管理员张一一',
          action: '添加了组织/机构',
          time: '17/08/2025 09:10:38',
          type: 'create',
        },
      ];
      setOperationLogs(mockLogs);
      setLogsLoading(false);
    }, 1000);
  };

  const handleSubmit = async () => {
    try {
      setConfirmLoading(true);
      const values = await form.validateFields();

      if (editingOrganization) {
        // 编辑组织
        const updateData: UpdateOrganizationRequest = {
          id: editingOrganization.id,
          organization_name: values.organization_name,
          phone: values.phone,
          email: values.email,
          status: values.status,
        };

        const response = await updateOrganization(updateData);
        if (response.code === 0) {
          message.success('组织/机构信息更新成功');
          setIsModalOpen(false);
          form.resetFields();
          // 重新获取组织列表
          fetchOrganizationList(pagination.current, pagination.pageSize);
        } else {
          message.error(response.message || '更新组织失败');
        }
      } else {
        // 新增组织
        const createData: CreateOrganizationRequest = {
          organization_name: values.organization_name,
          phone: values.phone,
          email: values.email,
        };

        const response = await createOrganization(createData);
        if (response.code === 0) {
          message.success('组织/机构创建成功');
          setIsModalOpen(false);
          form.resetFields();
          // 重新获取组织列表
          fetchOrganizationList(pagination.current, pagination.pageSize);
        } else {
          message.error(response.message || '创建组织失败');
        }
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteOrganization({ id });
      if (response.code === 0) {
        message.success('组织删除成功');
        // 重新获取组织列表
        fetchOrganizationList(pagination.current, pagination.pageSize);
      } else {
        message.error(response.message || '删除组织失败');
      }
    } catch (error) {
      console.error('删除组织失败:', error);
      message.error('删除组织失败');
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={2}>组织/机构管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={organizations}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1000 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      {/* 添加/编辑弹窗 */}
      <Modal
        title={editingOrganization ? '编辑组织/机构' : '添加组织/机构'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={confirmLoading}
        destroyOnHidden
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="organization_name" label="组织名称" rules={[{ required: true, message: '请输入组织/机构名称' }]}>
            <Input placeholder="请输入组织/机构名称" />
          </Form.Item>

          <Form.Item name="phone" label="联系电话">
            <Input placeholder="请输入联系电话" />
          </Form.Item>

          <Form.Item name="describe" label="组织描述">
            <TextArea placeholder="请输入组织描述" maxLength={30} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title="详情"
        open={isDetailModalOpen}
        onCancel={() => setIsDetailModalOpen(false)}
        footer={[
          <Button key="edit" type="primary" onClick={() => handleEditFromDetail(detailOrganization!)}>
            编辑
          </Button>,
        ]}
        width={800}
        destroyOnHidden
      >
        {detailOrganization && (
          <div>
            <Descriptions column={1} labelStyle={{ width: '120px' }}>
              <Descriptions.Item label="组织名称">{detailOrganization.organization_name}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{detailOrganization.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{detailOrganization.created_at}</Descriptions.Item>
              <Descriptions.Item label="组织描述">{detailOrganization.describe || '-'}</Descriptions.Item>
              {/* <Descriptions.Item label="用户数量">
                <Tag color="blue">{detailOrganization.users ? detailOrganization.users.length : 0} 个用户</Tag>
              </Descriptions.Item> */}
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrganizationManagement;
